/**
 * @file shared/uploadLimits.js
 * @description FUENTE ÚNICA de los límites de subida de ficheros (#266).
 *
 * Antes de este módulo el contrato vivía a mano en ~21 puntos repartidos en 6 catálogos y
 * 3 superficies, sincronizados sólo por comentarios del tipo "actualizar aquí Y en el backend".
 * Ya se rompió dos veces: #239 tuvo que realinear tamaños, y `errorHandler` llegó a anunciar
 * "Maximum 10 files" cuando el tope real era 5.
 *
 * ## La distinción que sostiene el diseño: IDENTIDAD vs POLÍTICA
 *
 * - **Identidad** (`FILE_TYPES`, `BROWSER_MIME_ALIASES`): qué *es* un fichero — su MIME canónico,
 *   sus extensiones, y los alias que algunos navegadores declaran. Es universal: un `.docx` es un
 *   `.docx` en cualquier superficie. Por eso NO lleva tamaños.
 * - **Política** (`SURFACES`): qué se *permite* — qué tipos, qué tamaño, cuántos ficheros, cuánto
 *   en total. Es **por superficie, siempre**, y cada una lleva su tabla COMPLETA.
 *
 * Los límites del chat y los de una colección de conocimiento **no son el mismo límite y no
 * tienen por qué converger nunca**: los gobiernan restricciones distintas aguas abajo (el chat,
 * su pipeline de adjuntos; las colecciones, la ingesta RAG). Hoy la mayoría de valores coinciden
 * por herencia de #239 — eso es una *observación*, no una regla. Nada aquí debe reintroducir una
 * tabla común "con excepciones": el día que la ingesta RAG imponga PDF a 20 MB mientras el chat
 * sigue en 50, ese modelo obliga a forkear el catálogo o a mentir en un lado.
 *
 * Añadir una cuarta superficie es añadir una entrada a `SURFACES`, no renegociar nada.
 *
 * ## Las tres indexaciones (deliberadas, no las fusiones)
 *
 * | Consumidor                         | Compara contra          |
 * |------------------------------------|-------------------------|
 * | `server/middleware/fileValidation` | MIME **sniffeado** del contenido (`file-type`) |
 * | `ChatFileHandler` / `rolRoutes` / `rolFileService` | MIME **declarado por el navegador** |
 * | `src/utils/fileValidation`         | **extensión** del nombre |
 *
 * El backend no se fía de lo que declara el cliente: sniffa los bytes. Por eso los alias
 * (hoy sólo `application/x-subrip`) existen únicamente del lado que ve lo que el navegador
 * declara, y no deben aparecer en la vista sniffeada.
 *
 * ## Regla de admisión (#728)
 *
 * **Sólo se acepta lo que algún motor sabe tratar.** El Switch `Derivar segun extension` de los
 * workflows de N8N no tiene rama de fallback: un tipo aceptado sin rama no se ignora — deja el
 * hilo colgado para siempre, porque nadie cierra el ciclo. Por eso se retiraron `.zip`, `.pptx`
 * y el audio (`.mp3`, `.wav`, `.webm`, `.ogg`). Antes de añadir un tipo aquí, comprueba que el
 * Switch correspondiente lo enruta; hay un test que lo verifica.
 *
 * CommonJS plano y sin dependencias a propósito: lo importan el backend (Node) y los tres
 * frontends (webpack, que lo procesa con la regla "Process any JS outside of the app with Babel").
 * Para que el frontend pueda importarlo hace falta que `config-overrides.js` quite el
 * `ModuleScopePlugin` de CRA — sin eso el build falla con "falls outside of the project src/".
 *
 * @typedef {Object} FileTypeIdentity
 * @property {string[]} extensions Extensiones sin punto, en minúscula.
 * @property {string} label Nombre legible, para mensajes de error.
 *
 * @typedef {Object} SurfacePolicy
 * @property {number} maxFiles Máximo de ficheros por petición.
 * @property {number} maxTotalSize Máximo agregado por petición, en bytes.
 * @property {Record<string, number>} maxSize Tamaño máximo por MIME canónico, en bytes. Completo
 *   para esta superficie: los tipos que no aparecen aquí NO se admiten en ella.
 * @property {number} [defaultMaxSize] Tope grueso por fichero cuando la capa que valida no
 *   distingue el tipo (p. ej. `limits.fileSize` de multer).
 */

const MB = 1024 * 1024;

/* ────────────────────────────────────────────────────────────────────────────
 * IDENTIDAD — compartida por todas las superficies. Sin tamaños.
 * ──────────────────────────────────────────────────────────────────────────── */

/** @type {Record<string, FileTypeIdentity>} */
const FILE_TYPES = {
  // Imágenes
  'image/jpeg': { extensions: ['jpg', 'jpeg'], label: 'JPEG' },
  'image/png': { extensions: ['png'], label: 'PNG' },
  'image/gif': { extensions: ['gif'], label: 'GIF' },
  'image/webp': { extensions: ['webp'], label: 'WebP' },
  'image/bmp': { extensions: ['bmp'], label: 'BMP' },

  // Documentos
  'application/pdf': { extensions: ['pdf'], label: 'PDF' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extensions: ['docx'],
    label: 'DOCX'
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extensions: ['xlsx'],
    label: 'XLSX'
  },
  // XLS (Excel 97-2003). Lo procesa el MISMO nodo que el XLSX —`Excel Multi-Hoja + Fórmulas`,
  // un Code con SheetJS que detecta BIFF por magic bytes—, así que comparte tope con él.
  // Ojo: `file-type` lo ve como `application/x-cfb`, el contenedor OLE2 que también usan .doc,
  // .ppt y .msi; el remapeo por extensión vive en `fileValidation.js` y es deliberadamente
  // estrecho por eso mismo.
  'application/vnd.ms-excel': {
    extensions: ['xls'],
    label: 'XLS'
  },
  // DOC (Word 97-2003). No es un DOCX con otra extensión: es un contenedor OLE2/CFB binario,
  // que python-docx rechaza. Lo lee el mismo `flock-document-converter`, que lo normaliza a
  // .docx con el LibreOffice que ya llevaba dentro antes de que ningún parser lo toque
  // (2026-09-11). `file-type` lo ve como `application/x-cfb`, igual que .xls, .ppt y .msi:
  // el remapeo por extensión de `fileValidation.js` sigue siendo deliberadamente estrecho.
  'application/msword': {
    extensions: ['doc'],
    label: 'DOC'
  },
  // PPTX. Su rama del extractor (`pptx to MD` → flock-document-converter) llevaba operativa en
  // los dos entornos desde antes de habilitarlo aquí —devuelve markdown con diapositivas, tablas
  // y notas del orador—, pero solo era alcanzable vía SharePoint: chat y rol lo tenían fuera
  // porque su `Derivar segun extension` no tenía rama y el Switch no lleva fallback (#728). Esa
  // rama se añadió a los 4 workflows (2 × 2 entornos) el 2026-09-03, ANTES que este catálogo.
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    extensions: ['pptx'],
    label: 'PPTX'
  },
  // PPT (PowerPoint 97-2003). Mismo caso que DOC: OLE2, normalizado a .pptx en el conversor.
  'application/vnd.ms-powerpoint': {
    extensions: ['ppt'],
    label: 'PPT'
  },

  // Texto y datos
  'text/plain': { extensions: ['txt', 'log', 'srt'], label: 'texto plano' },
  'text/markdown': { extensions: ['md'], label: 'Markdown' },
  'text/csv': { extensions: ['csv'], label: 'CSV' },
  'text/vtt': { extensions: ['vtt'], label: 'WebVTT' },
  'application/json': { extensions: ['json'], label: 'JSON' },
  // HTML. No tiene magic bytes, así que entra por la rama de texto de `fileValidation.js`
  // (allí está su extensión). El extractor lo pasa por `parse html`, el strip-tags de #321.
  'text/html': { extensions: ['html'], label: 'HTML' }
};

/**
 * MIMEs que declaran algunos navegadores y que NO son el MIME canónico.
 *
 * Existen sólo del lado que ve lo que el cliente declara (frontend, `fileFilter` de multer).
 * La capa que sniffa el contenido con `file-type` nunca los ve — recibe el canónico — así que
 * **no deben aparecer en `allowedTypesUnion()`**.
 *
 * - `application/x-subrip`: alterno para `.srt` (que además viaja como `text/plain`).
 *
 * Se retiró `application/x-zip-compressed` (lo que manda Chrome en Windows para un `.zip`) al
 * dejar de aceptarse el ZIP en ninguna superficie: un alias hacia un tipo que nadie admite es
 * ruido que sugiere una capacidad inexistente.
 *
 * @type {Record<string, string>}
 */
const BROWSER_MIME_ALIASES = {
  'application/x-subrip': 'text/plain'
};

/* ────────────────────────────────────────────────────────────────────────────
 * POLÍTICA — por superficie. Cada tabla es COMPLETA para la suya.
 *
 * Los comentarios del porqué se conservan literalmente: son la razón de que cada número sea
 * el que es, y sin ellos el siguiente que los toque no sabrá qué está arriesgando.
 * ──────────────────────────────────────────────────────────────────────────── */

/** @type {Record<string, SurfacePolicy>} */
const SURFACES = {
  /**
   * CHAT — adjuntos a un mensaje. `server/routes/threadRoutes.js` + `ChatFileHandler.ts`.
   */
  chat: {
    maxFiles: 5,
    maxTotalSize: 100 * MB,
    defaultMaxSize: 50 * MB, // tope grueso de multer; el fino por tipo lo aplica fileValidation
    maxSize: {
      'image/jpeg': 10 * MB,
      'image/png': 10 * MB,
      'image/gif': 5 * MB,
      'image/webp': 10 * MB,
      // PDF 50MB: libros técnicos densos (caso jalfez 2026-06-01). N8N usa binary mode s3 +
      // workers con heap 4GB, aguanta extracción de PDFs grandes sin presión de memoria.
      'application/pdf': 50 * MB,
      // DOCX en 15MB. El porqué escrito aquí decía «pasa por Heroku (router timeout 30s hard)»
      // y el 2026-09-11 ya no era cierto: el extractor vivo llama a
      // `http://flock-document-converter:5000/docx-to-md`, el servicio interno, en los dos
      // entornos. El tope se mantiene porque nadie ha medido cuál es el nuevo techo real —
      // pero la razón que lo sostenía ya no existe, y eso es lo que hay que medir antes de
      // tocarlo, no volver a copiar el número.
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 15 * MB,
      // DOC comparte destino con DOCX (el conversor lo normaliza y sigue por el mismo camino),
      // así que comparte tope. La normalización añade ~1,8s para un .doc de 1,57MB (medido).
      'application/msword': 15 * MB,
      // XLSX 20MB: el parsing multi-hoja corre en un Code node del worker N8N (en heap).
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 20 * MB,
      // XLS comparte motor con XLSX (mismo Code node con SheetJS) ⇒ mismo tope.
      'application/vnd.ms-excel': 20 * MB,
      // PPTX 20MB: medido en 0,1 s para un pptx de 35 KB contra `flock-document-converter`
      // (el mismo servicio interno al que va hoy el DOCX).
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 20 * MB,
      // PPT comparte destino y tope con PPTX: el conversor lo normaliza a .pptx.
      'application/vnd.ms-powerpoint': 20 * MB,
      'image/bmp': 10 * MB,
      'text/plain': 5 * MB,
      'text/markdown': 5 * MB,
      'text/csv': 10 * MB,
      'text/html': 5 * MB,
      'text/vtt': 5 * MB,
      'application/json': 5 * MB
    }
  },

  /**
   * ROL — ficheros de una colección de conocimiento (Knowledge Manager).
   * `server/routes/rolRoutes.js` + `server/services/rolFileService.js` + `src/utils/fileValidation.ts`.
   * Su catálogo es casi el del chat: los dos están limitados por lo que sus motores saben enrutar.
   */
  rol: {
    maxFiles: 20,
    maxTotalSize: 100 * MB,
    defaultMaxSize: 50 * MB, // gate grueso (PDFs técnicos grandes)
    maxSize: {
      'image/jpeg': 10 * MB,
      'image/png': 10 * MB,
      'image/gif': 5 * MB,
      'image/webp': 10 * MB,
      // El PDF no tenía entrada propia en `TYPE_MAX_SIZE` y caía al global de 50MB; se hace
      // explícito aquí sin cambiar el valor efectivo.
      'application/pdf': 50 * MB,
      // Office — limitados por el extractor (XLSX: heap del worker N8N; DOCX: ver la nota de la
      // superficie `chat`, su justificación caducó)
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 15 * MB,
      // DOC comparte destino y por tanto tope con DOCX (ver superficie `chat`).
      'application/msword': 15 * MB,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 20 * MB,
      // XLS comparte motor con XLSX (mismo Code node con SheetJS) ⇒ mismo tope.
      'application/vnd.ms-excel': 20 * MB,
      // PPTX 20MB: medido en 0,1 s para un pptx de 35 KB contra `flock-document-converter`
      // (el mismo servicio interno al que va hoy el DOCX).
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 20 * MB,
      // PPT comparte destino y tope con PPTX: el conversor lo normaliza a .pptx.
      'application/vnd.ms-powerpoint': 20 * MB,
      'image/bmp': 10 * MB,
      'text/plain': 5 * MB,
      'text/markdown': 5 * MB,
      'text/csv': 10 * MB,
      'text/html': 5 * MB,
      'text/vtt': 5 * MB,
      'application/json': 5 * MB
    }
  },

  /**
   * REPORTES — captura de pantalla del FAB de reportes. `server/routes/reportRoutes.js`.
   * Forma distinta: no hay selector de fichero, la captura la genera el propio frontend con
   * `canvas.toBlob`. Tampoco pasa por `validateUploadedFiles` (no hay magic bytes en esa ruta).
   * Está aquí sobre todo para que `errorHandler` pueda decir la verdad sobre ella.
   */
  reports: {
    maxFiles: 1,
    maxTotalSize: 5 * MB,
    defaultMaxSize: 5 * MB,
    maxSize: {
      'image/png': 5 * MB,
      'image/jpeg': 5 * MB,
      'image/webp': 5 * MB,
      'image/gif': 5 * MB
    }
  }
};

/* ────────────────────────────────────────────────────────────────────────────
 * DERIVADORES — funciones puras. Cada consumidor toma la vista de SU indexación.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * @param {string} surface
 * @returns {SurfacePolicy}
 */
function policyFor(surface) {
  const policy = SURFACES[surface];
  if (!policy) {
    throw new Error(`[uploadLimits] Superficie desconocida: "${surface}". Conocidas: ${Object.keys(SURFACES).join(', ')}`);
  }
  return policy;
}

/**
 * Tamaños indexados por el MIME que **declara el navegador**, incluidos los alias.
 * Para `ChatFileHandler.FILE_SIZE_LIMITS` y equivalentes.
 *
 * @param {string} surface
 * @returns {Record<string, number>}
 */
function sizeByBrowserMime(surface) {
  const { maxSize } = policyFor(surface);
  /** @type {Record<string, number>} */
  const out = {};
  for (const mime of Object.keys(maxSize)) out[mime] = maxSize[mime];
  for (const alias of Object.keys(BROWSER_MIME_ALIASES)) {
    const canonical = BROWSER_MIME_ALIASES[alias];
    if (canonical in maxSize) out[alias] = maxSize[canonical];
  }
  return out;
}

/**
 * Tamaños indexados por **extensión** (sin punto). Para `src/utils/fileValidation.TYPE_MAX_SIZE`.
 *
 * @param {string} surface
 * @returns {Record<string, number>}
 */
function sizeByExtension(surface) {
  const { maxSize } = policyFor(surface);
  /** @type {Record<string, number>} */
  const out = {};
  for (const mime of Object.keys(maxSize)) {
    const identity = FILE_TYPES[mime];
    if (!identity) continue;
    for (const ext of identity.extensions) out[ext] = maxSize[mime];
  }
  return out;
}

/**
 * Lista de MIMEs aceptables comparando contra lo que **declara el navegador**: los canónicos de
 * la superficie más sus alias. Para el `fileFilter` de multer y para `ALLOWED_MIME_TYPES`.
 *
 * @param {string} surface
 * @returns {string[]}
 */
function browserMimeAllowlist(surface) {
  const { maxSize } = policyFor(surface);
  const canonical = Object.keys(maxSize);
  const aliases = Object.keys(BROWSER_MIME_ALIASES).filter(
    alias => BROWSER_MIME_ALIASES[alias] in maxSize
  );
  return canonical.concat(aliases);
}

/**
 * Extensiones aceptadas por la superficie, con punto y separadas por comas (`.txt,.md,...`).
 *
 * @param {string} surface
 * @returns {string}
 */
function acceptExtensions(surface) {
  return Object.keys(sizeByExtension(surface)).map(ext => `.${ext}`).join(',');
}

/**
 * Valor para el atributo `accept` de un `<input type="file">`: MIME canónico seguido de sus
 * extensiones, intercalados.
 *
 * El picker de Android (API ≤29) resuelve MIME types, no extensiones: usar sólo extensiones deja
 * el selector en cámara/galería en algunos OEM antiguos (#74/#92). Por eso van los dos.
 *
 * @param {string} surface
 * @returns {string}
 */
function acceptForInput(surface) {
  const { maxSize } = policyFor(surface);
  /** @type {string[]} */
  const parts = [];
  for (const mime of Object.keys(maxSize)) {
    const identity = FILE_TYPES[mime];
    if (!identity) continue;
    parts.push(mime);
    for (const ext of identity.extensions) parts.push(`.${ext}`);
  }
  for (const alias of Object.keys(BROWSER_MIME_ALIASES)) {
    if (BROWSER_MIME_ALIASES[alias] in maxSize) parts.push(alias);
  }
  return parts.join(',');
}

/**
 * Vista **sniffeada de UNA superficie**: `{ mime: { extensions, maxSize } }`.
 *
 * Es lo que debe consumir la validación por magic bytes cuando sabe de dónde viene la petición
 * (`req.uploadSurface`, que pone `tagUploadSurface`). Sin alias: aquí el MIME viene de sniffar
 * el contenido, no de lo que declara el cliente.
 *
 * @param {string} surface
 * @returns {Record<string, { extensions: string[], maxSize: number }>}
 */
function allowedTypesForSurface(surface) {
  const { maxSize } = policyFor(surface);
  /** @type {Record<string, { extensions: string[], maxSize: number }>} */
  const out = {};
  for (const mime of Object.keys(maxSize)) {
    const identity = FILE_TYPES[mime];
    if (!identity) continue;
    out[mime] = { extensions: identity.extensions.slice(), maxSize: maxSize[mime] };
  }
  return out;
}

/**
 * Vista **sniffeada** y **ciega a la superficie**: `{ mime: { extensions, maxSize } }` con la
 * unión de todas las superficies.
 *
 * Es el **fallback** para cuando no se sabe la superficie. Desde #724 `validateUploadedFiles`
 * usa `allowedTypesForSurface(req.uploadSurface)` cuando la ruta la ha declarado con
 * `tagUploadSurface(...)`, y sólo cae aquí si no viene — así una ruta nueva que se olvide de
 * declararla sigue validando (contra la unión, más permisivo) en vez de rechazarlo todo.
 *
 * Cuando dos superficies declaran tamaños distintos para el mismo tipo, la unión toma el **mayor**:
 * es un gate grueso que corre junto al fino de cada superficie, y así nunca resulta más estricto
 * que hoy. Sin alias: aquí el MIME siempre viene de sniffar el contenido.
 *
 * @returns {Record<string, { extensions: string[], maxSize: number }>}
 */
function allowedTypesUnion() {
  /** @type {Record<string, { extensions: string[], maxSize: number }>} */
  const out = {};
  for (const surface of Object.keys(SURFACES)) {
    const { maxSize } = SURFACES[surface];
    for (const mime of Object.keys(maxSize)) {
      const identity = FILE_TYPES[mime];
      if (!identity) continue;
      if (!out[mime]) {
        out[mime] = { extensions: identity.extensions.slice(), maxSize: maxSize[mime] };
      } else if (maxSize[mime] > out[mime].maxSize) {
        out[mime].maxSize = maxSize[mime];
      }
    }
  }
  return out;
}

module.exports = {
  MB,
  FILE_TYPES,
  BROWSER_MIME_ALIASES,
  SURFACES,
  policyFor,
  sizeByBrowserMime,
  sizeByExtension,
  browserMimeAllowlist,
  acceptExtensions,
  acceptForInput,
  allowedTypesForSurface,
  allowedTypesUnion
};
